import { useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

// MUI
import { Box, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';

// Components
import SiteBranding from '../../base/branding/sitebranding';
import { useAppSelector, useAppDispatch } from '../../../redux/hook';
import { authLogin } from '../../../redux/slices/authSlice';
import { getEnvLabel } from '../../../helpers/determine-env';

// Services
import AuthService from '../../../services/auth.service';
import { AuthRequest } from '../../../requestModels/authRequest';

// Styles
import './Auth.scss';

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const authState = useAppSelector((state) => state.auth);

  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [notification, setNotification] = useState('');

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotIsSuccess, setForgotIsSuccess] = useState(false);

  const envLabel = getEnvLabel();

  const handleLogin = (e?: React.FormEvent) => {
    e?.preventDefault();
    setNotification('');

    const urlToken = new URLSearchParams(window.location.search).get('token');
    const authPayload = urlToken ? { token: urlToken } : { email: userName, password };

    dispatch(authLogin(authPayload as AuthRequest)).then((res: any) => {
      const { status, data } = res.payload;
      if (status === 200) {
        navigate('/dashboard');
      } else if (status === 401) {
        setNotification('Wrong username or password. Try again.');
      } else if (status === 403) {
        setNotification(data || 'Forbidden');
      } else {
        setNotification(res.payload.statusText || 'Login error');
      }
    });
  };

  const handleForgotSubmit = async () => {
    setForgotMessage('');
    setForgotIsSuccess(false);
    if (!forgotEmail) {
      setForgotMessage('Please enter your email');
      return;
    }
    try {
      setForgotLoading(true);
      const res: any = await AuthService.resetPassword(forgotEmail);
      if (res && res.status === 200) {
        setForgotIsSuccess(true);
        setForgotMessage('Reset password link has been sent to your email.');
        setTimeout(() => {
          setForgotOpen(false);
          setForgotEmail('');
          setForgotMessage('');
          setForgotIsSuccess(false);
        }, 2200);
      } else {
        setForgotMessage(res?.statusText || 'Reset request failed');
      }
    } catch (err: any) {
      setForgotMessage(err?.message || 'Reset request failed');
    } finally {
      setForgotLoading(false);
    }
  };

  useEffect(() => {
    AuthService.handleSSOCallback();
  }, []);

  return (
    <div className='auth auth--split'>
      {/* Hero panel (background image on desktop, hidden on phones) */}
      <aside className='auth-hero'>
        <h2 className='auth-hero__tagline'>Track. Manage. Deliver.</h2>
        <p className='auth-hero__sub'>Sign in to continue to your dashboard.</p>
      </aside>

      {/* Form panel */}
      <section className='auth-panel'>
        <div className='auth-card' style={{ border: 'none', boxShadow: 'none', padding: 0, maxWidth: 400 }}>
          <div className='auth-brand'>
            <SiteBranding />
            {envLabel && (
              <Box className='sidebar-env-badge' sx={{ ml: 2 }}>
                {envLabel}
              </Box>
            )}
          </div>

          <h1 className='auth-title'>Welcome back</h1>
          <p className='auth-subtitle'>Sign in to your account</p>

          {notification && <div className='auth-notice auth-notice--error'>{notification}</div>}

          <form className='auth-form' onSubmit={handleLogin}>
            <TextField
              variant='outlined'
              autoFocus
              label='Username'
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              fullWidth
              required
              size='small'
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              variant='outlined'
              type='password'
              label='Password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              size='small'
              InputLabelProps={{ shrink: true }}
            />

            <div className='auth-forgot-row'>
              <button type='button' className='auth-link-btn' onClick={() => setForgotOpen(true)}>
                Forgot password?
              </button>
            </div>

            <LoadingButton
              className='auth-btn auth-btn--primary'
              loading={authState.isLoginLoading}
              disabled={authState.isLoginLoading}
              type='submit'
              fullWidth
              disableElevation
              variant='contained'
            >
              Login
            </LoadingButton>

            <div className='auth-divider'>
              <span>or</span>
            </div>

            <Button className='auth-btn auth-btn--outlined' variant='outlined' fullWidth onClick={() => AuthService.loginWithSSO()}>
              Continue with SSO
            </Button>
          </form>

          <div className='auth-switch'>
            Don&apos;t have an account?
            <RouterLink to='/register'>Register</RouterLink>
          </div>
        </div>

        {/* Forgot password dialog */}
        <Dialog open={forgotOpen} onClose={() => setForgotOpen(false)} PaperProps={{ sx: { borderRadius: '12px', border: '2px solid #dfe6ef' } }}>
          <DialogTitle sx={{ fontSize: 18, fontWeight: 600 }}>Reset Password</DialogTitle>
          <DialogContent>
            <Box sx={{ minWidth: { xs: 260, sm: 360 }, mt: 1 }}>
              <TextField
                autoFocus
                fullWidth
                size='small'
                label='Email'
                type='email'
                variant='outlined'
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              {forgotMessage && (
                <div
                  className={`auth-notice ${forgotIsSuccess ? 'auth-notice--success' : 'auth-notice--info'}`}
                  style={{ marginTop: 12, marginBottom: 0 }}
                >
                  {forgotMessage}
                </div>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setForgotOpen(false)} className='auth-btn auth-btn--outlined'>
              Cancel
            </Button>
            <LoadingButton loading={forgotLoading} onClick={handleForgotSubmit} variant='contained' className='auth-btn auth-btn--primary'>
              Send Reset Link
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </section>
    </div>
  );
}
